import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, History, ClipboardCheck, CalendarPlus } from 'lucide-react';
import { CompanyFollowUp, FollowUpTrialPhase, TrialExtension } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const phoneStatusLabels: Record<string, string> = {
  connected: 'Terhubung',
  not_connected: 'Tidak terhubung',
  invalid: 'Nomor tidak valid',
};

const outcomeLabels: Record<string, string> = {
  closing: 'Closing',
  call_back_later: 'Minta dihubungi kembali',
  other: 'Lainnya',
};

const phaseLabels: Record<FollowUpTrialPhase, { label: string; hint: string; className: string }> = {
  trial_awal: { label: 'Trial awal', hint: 'Trial masih berjalan', className: 'bg-emerald-100 text-emerald-800' },
  trial_akhir: { label: 'Trial akhir', hint: '24 jam terakhir sebelum trial habis', className: 'bg-amber-100 text-amber-800' },
  winback: { label: 'Pasca-trial', hint: 'Trial sudah habis', className: 'bg-rose-100 text-rose-800' },
};

interface FollowUpPanelProps {
  companyId: string;
  followUps: CompanyFollowUp[];
  canSubmit: boolean;
  onSubmitted: () => void;
  trialPhase?: FollowUpTrialPhase | null;
  messagePreview?: string;
  trialEndsAt?: string | null;
  canExtendTrial?: boolean;
  extendTrialBlockedReason?: string | null;
  trialExtensions?: TrialExtension[];
}

const EXTEND_OPTIONS: Array<3 | 7> = [3, 7];

type PhoneStatus = 'connected' | 'not_connected' | 'invalid';
type OutcomeStatus = 'closing' | 'call_back_later' | 'other';

const FollowUpPanel: React.FC<FollowUpPanelProps> = ({
  companyId,
  followUps,
  canSubmit,
  onSubmitted,
  trialPhase,
  messagePreview,
  trialEndsAt,
  canExtendTrial = false,
  extendTrialBlockedReason,
  trialExtensions = [],
}) => {
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>('connected');
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatus>('closing');
  const [notes, setNotes] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [waLoading, setWaLoading] = useState(false);
  const [waError, setWaError] = useState<string | null>(null);

  const [extendingDays, setExtendingDays] = useState<number | null>(null);
  const [extendError, setExtendError] = useState<string | null>(null);

  const phaseInfo = trialPhase ? phaseLabels[trialPhase] : null;

  const extendTrial = useCallback(async (days: 3 | 7) => {
    if (!window.confirm(`Perpanjang trial company ini ${days} hari?`)) return;
    setExtendingDays(days);
    setExtendError(null);
    try {
      await api.extendCompanyTrial(companyId, days);
      onSubmitted();
    } catch (err: any) {
      setExtendError(err?.response?.data?.message || 'Gagal memperpanjang trial.');
    } finally {
      setExtendingDays(null);
    }
  }, [companyId, onSubmitted]);

  const formatDateTime = (dateString: string): string =>
    new Date(dateString).toLocaleString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const openWhatsApp = useCallback(async () => {
    setWaLoading(true);
    setWaError(null);
    try {
      const { followUpUrl } = await api.getCompanyFollowUpUrl(companyId);
      window.open(followUpUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setWaError(err?.response?.data?.message || 'Gagal membuka WhatsApp.');
    } finally {
      setWaLoading(false);
    }
  }, [companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (phoneStatus === 'connected' && outcomeStatus === 'other' && !notes.trim()) {
      setFormError('Catatan wajib diisi ketika memilih "Lainnya".');
      return;
    }

    const body: { phoneStatus: string; outcomeStatus?: string; notes?: string } = { phoneStatus };
    if (phoneStatus === 'connected') {
      body.outcomeStatus = outcomeStatus;
    }
    if (notes.trim()) {
      body.notes = notes.trim();
    }

    setSubmitting(true);
    try {
      await api.createCompanyFollowUp(companyId, body, evidence);
      setNotes('');
      setPhoneStatus('connected');
      setOutcomeStatus('closing');
      setEvidence(null);
      setFileInputKey(k => k + 1);
      onSubmitted();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Gagal menyimpan follow-up.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (phoneStatus !== 'connected') {
      setFormError(null);
    }
  }, [phoneStatus]);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {phaseInfo && (
          <div className="flex flex-wrap items-center gap-2">
            {phaseInfo && (
              <span title={phaseInfo.hint} className={`px-2 py-1 rounded text-xs font-medium ${phaseInfo.className}`}>
                {phaseInfo.label}
              </span>
            )}
            <span className="text-xs text-gray-400">menentukan pesan yang dikirim</span>
          </div>
        )}

        {messagePreview && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Pesan yang akan terkirim</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{messagePreview}</p>
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">Trial</p>
              <p className="text-xs text-gray-500">
                {trialEndsAt ? `Berlaku sampai ${formatDateTime(trialEndsAt)}` : 'Tidak ada trial aktif'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {EXTEND_OPTIONS.map(days => (
                <button
                  key={days}
                  onClick={() => extendTrial(days)}
                  // canSubmit = full admin atau sales, sama persis dengan guard di backend.
                  disabled={!canExtendTrial || !canSubmit || extendingDays !== null}
                  title={canSubmit ? (extendTrialBlockedReason || undefined) : 'Peran Anda tidak bisa memperpanjang trial.'}
                  className="flex items-center gap-1.5 px-3 py-2 border border-primary-600 text-primary-700 text-sm font-medium rounded-md hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <CalendarPlus className="h-4 w-4" />
                  {extendingDays === days ? 'Memproses...' : `Extend ${days} hari`}
                </button>
              ))}
            </div>
          </div>

          {!canExtendTrial && extendTrialBlockedReason && (
            <p className="mt-2 text-xs text-gray-500">{extendTrialBlockedReason}</p>
          )}
          {extendError && <p className="mt-2 text-sm text-red-700">{extendError}</p>}

          {trialExtensions.length > 0 && (
            <ul className="mt-3 pt-3 border-t border-gray-100 space-y-1">
              {trialExtensions.map(extension => (
                <li key={extension.id} className="text-xs text-gray-500">
                  +{extension.days} hari · {extension.byName || 'tidak diketahui'} · {formatDateTime(extension.at)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <button
            onClick={openWhatsApp}
            disabled={waLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <MessageCircle className="h-4 w-4" />
            {waLoading ? 'Membuka...' : 'WhatsApp'}
          </button>
          {waError && <div className="mt-2 text-sm text-red-700">{waError}</div>}
        </div>
      </div>

      {canSubmit && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-medium text-gray-900">Catat Follow-up</h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{formError}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status telepon</label>
              <div className="space-y-2">
                {(['connected', 'not_connected', 'invalid'] as PhoneStatus[]).map(status => (
                  <label key={status} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="phoneStatus"
                      value={status}
                      checked={phoneStatus === status}
                      onChange={() => setPhoneStatus(status)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    {phoneStatusLabels[status]}
                  </label>
                ))}
              </div>
            </div>

            {phoneStatus === 'connected' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Outcome</label>
                <select
                  value={outcomeStatus}
                  onChange={e => setOutcomeStatus(e.target.value as OutcomeStatus)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="closing">Closing</option>
                  <option value="call_back_later">Minta dihubungi kembali</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Catatan
                {phoneStatus === 'connected' && outcomeStatus === 'other' && <span className="text-red-500"> *</span>}
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Catatan tambahan..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Evidence (opsional, gambar)</label>
              <input
                key={fileInputKey}
                type="file"
                accept="image/*"
                onChange={e => setEvidence(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <LoadingSpinner size="sm" />}
                Simpan Follow-up
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
          <History className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-medium text-gray-900">Riwayat Follow-up</h3>
        </div>
        {followUps.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">Belum ada riwayat follow-up.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {followUps.map(fu => (
              <li key={fu.id} className="px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {phoneStatusLabels[fu.phoneStatus] || fu.phoneStatus}
                    </span>
                    {fu.outcomeStatus && (
                      <span className="text-sm text-gray-500"> · {outcomeLabels[fu.outcomeStatus] || fu.outcomeStatus}</span>
                    )}
                    {fu.notes && <p className="mt-1 text-sm text-gray-600">{fu.notes}</p>}
                    {fu.evidenceUrl && (
                      <a href={fu.evidenceUrl} target="_blank" rel="noreferrer" className="inline-block mt-2">
                        <img src={fu.evidenceUrl} alt="evidence" className="h-16 w-16 object-cover rounded border border-gray-200" />
                      </a>
                    )}
                    {fu.byName && <p className="mt-1 text-xs text-gray-400">oleh {fu.byName}</p>}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(fu.at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default FollowUpPanel;
